import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useApi from '../api';
import { formatDate } from '../utils/dateFormatter';
import { Table, TableHead, TableRow, TableBody, TableHeader, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LoadingState } from '@/components/ui/LoadingState';
import { BarChart3, DollarSign, Eye, Globe, Linkedin, Sparkles, UserRound, Users } from 'lucide-react';
import SalesforceIcon from '../icons/salesforce_logo.png';
import HubspotIcon from '../icons/hubspot_logo.webp';
import { ConfirmedBadge, Account as AccountRow } from './AccountsList';
import AccountFavicon from './AccountFavicon';
import AccountActivity from './AccountActivity';
import AccountObjectives from './AccountObjectives';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { StageBadge, stageTone, useLifecycleStages, LifecycleStageDef } from './lifecycleStages';

interface AccountContact {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  job_title?: string | null;
  source?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
}

interface AccountVisitorProfile {
  id: string;
  visitor_id?: string | null;
  lead_id?: string | null;
  provenance: 'contact' | 'enriched';
  person_name?: string | null;
  company_name?: string | null;
  source_latest?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  session_count?: number;
  conversation_count?: number;
  last_seen_at?: string | null;
}

interface AccountConversation {
  id: string;
  visitor_id?: string | null;
  lead_id?: string | null;
  status?: string | null;
  interacted?: boolean;
  created_at?: string | null;
  last_activity?: string | null;
}

interface CompanyFacts {
  name?: string | null;
  domain?: string | null;
  website_url?: string | null;
  industry?: string | null;
  employee_count?: string | number | null;
  estimated_revenue?: string | null;
  linkedin_url?: string | null;
  description?: string | null;
}

interface AccountDetailResponse {
  account: AccountRow;
  company?: CompanyFacts;
  contacts: AccountContact[];
  visitor_profiles: AccountVisitorProfile[];
  recent_conversations: AccountConversation[];
}

const ProvenanceBadge = ({ provenance }: { provenance: 'contact' | 'enriched' }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        {provenance === 'contact' ? (
          <span className="inline-flex items-center rounded-xl px-2 py-0.5 text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-500/10">
            Contact
          </span>
        ) : (
          <span className="inline-flex items-center rounded-xl px-2 py-0.5 text-xs font-medium text-muted-foreground bg-muted">
            Enriched
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent>
        {provenance === 'contact'
          ? 'Linked to an identified contact'
          : 'Association is enrichment-based — this visitor has not shared a work email'}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

function StageEditor({
  account,
  stages,
  onChange,
}: {
  account: AccountRow;
  stages: LifecycleStageDef[];
  onChange: (stage: string) => void;
}) {
  const currentKey = account.stage || 'anonymous';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 rounded-xl px-2 py-0.5 text-xs font-semibold ${stageTone(
            stages.find((s) => s.key === currentKey)?.color
          )} hover:opacity-80 transition-opacity`}
          title={
            account.stage_source
              ? `Set by ${account.stage_source}${account.stage_updated_at ? ` · ${formatDate(account.stage_updated_at)}` : ''}`
              : 'Set lifecycle stage'
          }
        >
          {stages.find((s) => s.key === currentKey)?.label || currentKey}
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {stages.map((stage) => (
          <DropdownMenuItem
            key={stage.key}
            onClick={() => stage.key !== currentKey && onChange(stage.key)}
            className="gap-2"
          >
            <StageBadge stageKey={stage.key} stages={stages} />
            {stage.key === currentKey && <Check className="h-3.5 w-3.5 ml-auto" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Account() {
  const { id } = useParams();
  const { get, patch } = useApi();
  const [detail, setDetail] = useState<AccountDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { stages } = useLifecycleStages();

  const setStage = async (stage: string) => {
    if (!detail) return;
    const previous = detail.account;
    // Optimistic update, rolled back on failure.
    setDetail({ ...detail, account: { ...previous, stage, stage_source: 'manual' } });
    try {
      const response = await patch(`/accounts/${id}`, { stage });
      setDetail((current) =>
        current ? { ...current, account: { ...current.account, ...response.data } } : current
      );
    } catch (error: any) {
      setDetail((current) => (current ? { ...current, account: previous } : current));
      toast.error(error?.data?.detail || 'Failed to update stage');
    }
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    get(`/accounts/${id}`)
      .then((response) => setDetail(response.data))
      .catch((error) => console.error('Error fetching account:', error))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <LoadingState label="Loading account..." className="min-h-[60vh]" />;
  }
  if (!detail) {
    return (
      <div className="flex w-full h-full items-center justify-center text-muted-foreground">
        Account not found.
      </div>
    );
  }

  const { account, company = {}, contacts, visitor_profiles: profiles, recent_conversations: conversations } = detail;
  const crmMatch = account.crm_match && account.crm_match.status === 'matched' ? account.crm_match : null;
  const crmIcon = crmMatch?.crm === 'salesforce' ? SalesforceIcon : crmMatch?.crm === 'hubspot' ? HubspotIcon : null;
  const crmLabel = crmMatch?.crm === 'salesforce' ? 'Salesforce' : crmMatch?.crm === 'hubspot' ? 'HubSpot' : null;
  const anonymousCount = profiles.filter((p) => p.provenance === 'enriched').length;

  return (
    <div className="flex w-full h-full overflow-y-auto scrollbar-thin">
      <div className="w-full max-w-7xl mx-auto p-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <AccountFavicon domain={account.domain || company.domain} className="h-7 w-7" />
              {account.name || account.domain || account.external_id}
              <ConfirmedBadge
                confirmedAt={account.first_confirmed_at}
                externalId={account.external_id}
              />
              <StageEditor account={account} stages={stages} onChange={setStage} />
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {account.domain && (
                <a
                  href={`https://${account.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:underline"
                >
                  <Globe className="h-3.5 w-3.5" /> {account.domain}
                </a>
              )}
              {account.external_id && (
                <span className="inline-flex items-center gap-1.5">
                  External ID: <code className="font-mono text-foreground">{account.external_id}</code>
                </span>
              )}
              <span>
                {contacts.length} contact{contacts.length === 1 ? '' : 's'}, {anonymousCount} anonymous visitor{anonymousCount === 1 ? '' : 's'}
              </span>
            </div>
            {(company.industry || company.employee_count || company.estimated_revenue || company.linkedin_url) && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                {company.industry && (
                  <span className="inline-flex items-center gap-1.5">
                    <BarChart3 className="h-3.5 w-3.5" /> {company.industry}
                  </span>
                )}
                {company.employee_count && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5" /> {company.employee_count} employees
                  </span>
                )}
                {company.estimated_revenue && (
                  <span className="inline-flex items-center gap-1.5">
                    <DollarSign className="h-3.5 w-3.5" /> {company.estimated_revenue}
                  </span>
                )}
                {company.linkedin_url && (
                  <a
                    href={company.linkedin_url.startsWith('http') ? company.linkedin_url : `https://${company.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-blue-500 hover:underline"
                  >
                    <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                  </a>
                )}
              </div>
            )}
            {company.description && (
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{company.description}</p>
            )}
          </div>
          {crmMatch && (
            <div className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
              {crmIcon && <img src={crmIcon} alt={crmLabel || 'CRM'} className="h-6 w-6 object-contain" />}
              <div className="text-sm">
                <div className="font-semibold">
                  {crmMatch.account_name || account.name || account.domain || account.external_id}
                </div>
                {(crmMatch.owner_name || crmMatch.owner_email) && (
                  <div className="text-muted-foreground inline-flex items-center gap-1">
                    <UserRound className="h-3 w-3" /> {crmMatch.owner_name || crmMatch.owner_email}
                  </div>
                )}
              </div>
              {crmMatch.account_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={crmMatch.account_url} target="_blank" rel="noopener noreferrer">
                    <Eye className="h-4 w-4" /> View
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* Fit */}
        {account.fit_score != null && (
          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold inline-flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" /> Fit
                  {account.research?.confidence && (
                    <span className="text-xs font-medium text-muted-foreground bg-muted rounded-xl px-2 py-0.5">
                      {account.research.confidence} confidence
                    </span>
                  )}
                </h2>
                {account.fit_reasoning && (
                  <p className="mt-2 text-sm text-muted-foreground">{account.fit_reasoning}</p>
                )}
                {!!account.research?.key_signals?.length && (
                  <ul className="mt-3 space-y-1 text-sm">
                    {account.research.key_signals.map((signal, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                        <span>{signal}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {!!account.research?.sources?.length && (
                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {account.research.sources.slice(0, 5).map((source, index) => {
                      let host = source;
                      try { host = new URL(source).hostname; } catch { /* keep raw */ }
                      return (
                        <a
                          key={index}
                          href={source}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {host}
                        </a>
                      );
                    })}
                  </div>
                )}
                {account.research?.researched_at && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    Researched {formatDate(account.research.researched_at)}
                  </div>
                )}
              </div>
              <div
                className={`flex flex-col items-center justify-center rounded-xl px-5 py-3 shrink-0 ${
                  account.fit_score >= 70
                    ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                    : account.fit_score >= 40
                      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                <span className="text-3xl font-bold tabular-nums">{account.fit_score}</span>
                <span className="text-xs font-medium">/ 100</span>
              </div>
            </div>
          </div>
        )}

        {/* Activity */}
        <AccountActivity accountId={account.id} />

        {/* Objectives */}
        <AccountObjectives accountId={account.id} />

        {/* Contacts */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">Contacts</h2>
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Job Title</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.length ? contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell>
                      <Link to={`/contacts/${contact.id}`} className="font-semibold hover:underline">
                        {contact.email || 'N/A'}
                      </Link>
                    </TableCell>
                    <TableCell>{[contact.first_name, contact.last_name].filter(Boolean).join(' ') || '-'}</TableCell>
                    <TableCell>{contact.job_title || '-'}</TableCell>
                    <TableCell>{contact.last_activity_at ? formatDate(contact.last_activity_at) : '-'}</TableCell>
                    <TableCell>{contact.created_at ? formatDate(contact.created_at) : '-'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-16 text-center text-muted-foreground">
                      No contacts yet — everything known about this account is enrichment-based.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Visitors */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">Visitors</h2>
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Visitor</TableHead>
                  <TableHead>Provenance</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Sessions</TableHead>
                  <TableHead>Last Seen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.length ? profiles.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell>
                      {profile.lead_id ? (
                        <Link to={`/contacts/${profile.lead_id}`} className="font-semibold hover:underline">
                          {profile.person_name || 'Identified visitor'}
                        </Link>
                      ) : (
                        <span>{profile.person_name || 'Anonymous visitor'}</span>
                      )}
                    </TableCell>
                    <TableCell><ProvenanceBadge provenance={profile.provenance} /></TableCell>
                    <TableCell>
                      {[profile.city, profile.region, profile.country].filter(Boolean).join(', ') || '-'}
                    </TableCell>
                    <TableCell>{profile.session_count ?? 0}</TableCell>
                    <TableCell>{profile.last_seen_at ? formatDate(profile.last_seen_at) : '-'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-16 text-center text-muted-foreground">
                      No visitor profiles linked to this account yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Recent sessions */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">Recent Sessions</h2>
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Interacted</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Last Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conversations.length ? conversations.map((conversation) => (
                  <TableRow key={conversation.id}>
                    <TableCell>
                      <Link to={`/sessions/${conversation.id}`} className="font-semibold hover:underline">
                        {conversation.id.slice(0, 8)}…
                      </Link>
                    </TableCell>
                    <TableCell>{conversation.status || '-'}</TableCell>
                    <TableCell>{conversation.interacted ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{conversation.created_at ? formatDate(conversation.created_at) : '-'}</TableCell>
                    <TableCell>{conversation.last_activity ? formatDate(conversation.last_activity) : '-'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-16 text-center text-muted-foreground">
                      No sessions from this account yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
